import { useState, useEffect, useCallback, useRef } from 'react';
import { AdminUserEventRecord } from '@/services/users';
import { RelativeTime } from '@/components/RelativeTime';
import {
  Users, 
  FileText,
  CalendarCheck,
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  Mail,
  Info
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { extraSubaccountBadges } from '@/lib/extra-subaccount-badges';
import { activityBadges, platformEventBadges, providerLabel } from '@/lib/platform-event-badges';
import { EVENT_CONFIG as AGENCY_EVENT_CONFIG } from '@/pages/agencies/components/ActivityLogTimeline';
import { useAuth } from '@/contexts/AuthContext';
import { webHref } from '@/lib/web-href';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

function TruncatedActivityBadge({ label, value, href }: { label: string; value: string; href?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [checkTruncation, value]);

  const valueNode = href ? (
    <a
      ref={ref as React.RefObject<HTMLAnchorElement>}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="truncate block max-w-[260px] text-primary underline underline-offset-2 hover:opacity-80"
    >
      {value}
    </a>
  ) : (
    <span ref={ref} className="truncate block max-w-[200px]">{value}</span>
  );

  const wrapper = (
    <Badge variant="secondary" className="font-medium text-xs shadow-none cursor-default max-w-full">
      <span className="text-muted-foreground mr-1 shrink-0">{label}:</span>
      {valueNode}
    </Badge>
  );

  if (!isTruncated) return wrapper;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>{wrapper}</TooltipTrigger>
        <TooltipContent side="top">
          {value}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface UserActivityTimelineProps {
  events: AdminUserEventRecord[];
  emptyMessage?: string;
}

const EVENT_CONFIG: Record<string, { title: string; icon: React.ReactNode; color: string }> = {
  signup: { title: 'User Signed Up', icon: <Users className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  plan_started: { title: 'Plan Started', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  plan_changes: { title: 'Plan Changed', icon: <CreditCard className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  downgraded: { title: 'Downgraded', icon: <Info className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  cancelled: { title: 'Cancelled', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  reactivated: { title: 'Reactivated', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  payment_failed: { title: 'Payment Failed', icon: <CreditCard className="h-4 w-4" />, color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  payment_recovered: { title: 'Payment Recovered', icon: <CreditCard className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  trial_converted: { title: 'Trial Converted', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  downgrade_offer_claimed: { title: 'Offer Claimed', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' },
  subaccount_created: { title: 'Subaccount Created', icon: <Building2 className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  team_member_invited: { title: 'Team Member Invited', icon: <Users className="h-4 w-4" />, color: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400' },
  form_created: { title: 'Form Created', icon: <FileText className="h-4 w-4" />, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  form_imported: { title: 'Form Imported', icon: <FileText className="h-4 w-4" />, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400' },
  form_import_failed: { title: 'Form Import Failed', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  calendar_added: { title: 'Calendar Added', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400' },
  logic_or_score_set: { title: 'Logic/Score Set', icon: <Info className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  split_test_started: { title: 'Split Test Started', icon: <Info className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },

  form_embedded: { title: 'Form Embedded', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  first_submission_received: { title: 'First Submission', icon: <Mail className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  first_lead_captured: { title: 'First Lead', icon: <Users className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  first_appointment_booked: { title: 'First Appointment', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400' },
  integration_connected: { title: 'Integration Connected', icon: <Info className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  integration_disconnected: { title: 'Integration Disconnected', icon: <Info className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  onboarding_completed: { title: 'Onboarding Completed', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  checkout_failed: { title: 'Checkout Failed', icon: <XCircle className="h-4 w-4" />, color: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  onboarding_call_offer_shown: { title: 'Setup Offer Shown', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  onboarding_call_booked: { title: 'Setup Call Booked', icon: <CalendarCheck className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  onboarding_call_skipped: { title: 'Setup Call Skipped', icon: <XCircle className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
  magic_link_sent: { title: 'Login Link Sent', icon: <Mail className="h-4 w-4" />, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' },
  magic_link_consumed: { title: 'Login Link Used', icon: <CheckCircle2 className="h-4 w-4" />, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400' },
  onboarding_step_changed: { title: 'Onboarding Step Changed', icon: <Info className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  forms_paused_past_due: { title: 'Forms Paused (Past Due)', icon: <XCircle className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  xray_funds_granted: { title: 'X-Ray Funds Granted', icon: <CreditCard className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  extra_subaccount_offer_shown: { title: 'Extra Subaccount Offer Shown', icon: <Info className="h-4 w-4" />, color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400' },
  extra_subaccount_agency_chosen: { title: 'Opened Plans from the Extra Subaccount Offer', icon: <Info className="h-4 w-4" />, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' },
  extra_subaccount_action_required: { title: 'Extra Subaccount Bank Check', icon: <CreditCard className="h-4 w-4" />, color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  extra_subaccount_purchased: { title: 'Extra Subaccount Purchased', icon: <Building2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  extra_subaccount_payment_failed: { title: 'Extra Subaccount Payment Failed', icon: <CreditCard className="h-4 w-4" />, color: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' },
  extra_subaccount_removed: { title: 'Extra Subaccount Removed', icon: <Building2 className="h-4 w-4" />, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' },
  extra_subaccount_unlocked: { title: 'Subaccount Unlocked', icon: <Building2 className="h-4 w-4" />, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' },
  extra_subaccounts_changed: { title: 'Extra Subaccounts Changed', icon: <Building2 className="h-4 w-4" />, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400' },
  default: { title: 'Unknown Event', icon: <Info className="h-4 w-4" />, color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' }
};

export function UserActivityTimeline({ events, emptyMessage = 'No activity recorded yet' }: UserActivityTimelineProps) {
  const { user } = useAuth();

  const renderMetadata = (event: AdminUserEventRecord) => {
    const md = event.metadata || {};
    if (Object.keys(md).length === 0 && !event.agencyName) return <span className="text-muted-foreground">—</span>;

    const badges: { label: string; value: string; href?: string }[] = [];

    const addBadge = (label: string, value: any, href?: string) => {
      if (value) badges.push({ label, value: String(value), href });
    };

    if (event.agencyName) addBadge('Agency', event.agencyName);

    // Surface the import URL first for imports (clickable, easy to investigate).
    if (event.eventType === 'form_import_failed' || event.eventType === 'form_imported') {
      const importUrl = md.inputUrl || md.sourceUrl;
      if (importUrl) addBadge('URL', importUrl, webHref(importUrl));
      if (md.platform) addBadge('Platform', md.platform);
      if (md.errorCode) addBadge('Code', md.errorCode);
      if (md.stage) addBadge('Stage', md.stage);
    }

    // Extra subaccounts: the count change, and why or how it happened.
    if (event.eventType.startsWith('extra_subaccount')) {
      for (const { label, value } of extraSubaccountBadges(md)) addBadge(label, value);
    }

    for (const { label, value } of platformEventBadges(event.eventType, md)) addBadge(label, value);

    if (md.formName) addBadge('Form', md.formName);
    if (md.subaccountName) addBadge('Subaccount', md.subaccountName);
    if (md.planName) addBadge('Plan', md.planName);
    if (md.provider) addBadge('Integration', providerLabel(md.provider));
    if (md.splitTestName) addBadge('Test', md.splitTestName);
    if (md.errorMessage) addBadge('Error', md.errorMessage);
    if (md.planKey || md.plan) addBadge('Plan', md.planKey || md.plan);
    for (const { label, value } of activityBadges(event, { timezone: user?.timezone })) addBadge(label, value);
    if (event.eventType === 'form_embedded' && md.embed_url) {
      addBadge('Website', md.embed_url, webHref(md.embed_url));
    }
    if (md.backfilled) {
      badges.push({ label: 'Source', value: 'Backfilled data' });
    }

    if (badges.length === 0) {
      const entries = Object.entries(md).filter(([k]) => k !== 'backfilled' && k !== 'htmlSnapshot');
      if (entries.length > 0) {
        addBadge(entries[0][0], entries[0][1]);
      }
    }

    return (
      <div className="flex flex-wrap gap-1.5 mt-1">
        {badges.map((b, i) => (
          <TruncatedActivityBadge key={i} label={b.label} value={b.value} href={b.href} />
        ))}
      </div>
    );
  };

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-muted-foreground py-12">
        <Clock className="h-12 w-12 mb-4 opacity-20" />
        <p className="text-lg font-medium text-foreground">{emptyMessage}</p>
        <p className="text-sm mt-1">Events will appear here as the user interacts with the platform.</p>
      </div>
    );
  }

  return (
    <div className="relative border-l-2 border-muted pl-6 ml-4 space-y-4 py-2">
      {events.map((event, index) => {
        // Events this list has no title for (the newer ones) take the agency Activity Log's.
        const config = EVENT_CONFIG[event.eventType] || AGENCY_EVENT_CONFIG[event.eventType] || EVENT_CONFIG.default;
        const isLast = index === events.length - 1;
        
        return (
          <div key={event.id} className="relative">
            <div className={`absolute -left-[39px] top-0 p-1.5 rounded-full ring-4 ring-card flex items-center justify-center ${config.color} bg-background`}>
              {config.icon}
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground text-base">{config.title}</span>
                </div>
                
                <div className="pt-1">
                  {renderMetadata(event)}
                </div>
              </div>
              
              <div className="shrink-0 text-sm text-muted-foreground">
                <RelativeTime date={event.createdAtUtc} />
              </div>
            </div>
            
            {!isLast && (
              <div className="mt-4 border-b border-border/50 hidden sm:block" />
            )}
          </div>
        );
      })}
    </div>
  );
}
